import { createContext, useContext, useRef, useCallback } from 'react'
import useBatchNoteLoader from './useBatchNoteLoader'

const NoteBatchLoaderContext = createContext(null)

export const NoteBatchLoaderProvider = ({ children, subsidiaryIds = [], kind = 'healthCheck-summary' }) => {
  const notesCacheRef = useRef(new Map())
  const updateCallbacksRef = useRef(new Map())

  // Callback when a note is loaded - MEMOIZED to prevent infinite loops
  const handleNoteLoaded = useCallback((subsidiaryId, kind, data) => {
    const key = `${subsidiaryId}-${kind}`
    notesCacheRef.current.set(key, data)
    
    // Notify all registered callbacks for this note
    const callbacks = updateCallbacksRef.current.get(key) || []
    callbacks.forEach(callback => callback(data))
  }, [])

  // Use batch loader hook
  const cache = useBatchNoteLoader(subsidiaryIds, {
    batchSize: 20,
    batchDelay: 300,
    initialDelay: 500,
    onNoteLoaded: handleNoteLoaded
  })

  // Register a callback for note updates
  const registerCallback = (subsidiaryId, kind, callback) => {
    const key = `${subsidiaryId}-${kind}`
    if (!updateCallbacksRef.current.has(key)) {
      updateCallbacksRef.current.set(key, [])
    }
    updateCallbacksRef.current.get(key).push(callback)

    // If note is already cached, call callback immediately
    if (notesCacheRef.current.has(key)) {
      callback(notesCacheRef.current.get(key))
    }

    // Return unregister function
    return () => {
      const callbacks = updateCallbacksRef.current.get(key) || []
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
    }
  }

  return (
    <NoteBatchLoaderContext.Provider value={{ registerCallback, notesCache: notesCacheRef.current }}>
      {children}
    </NoteBatchLoaderContext.Provider>
  )
}

export const useNoteBatchLoader = () => {
  const context = useContext(NoteBatchLoaderContext)
  if (!context) {
    // Return no-op functions if context is not available
    return {
      registerCallback: () => () => {},
      notesCache: new Map()
    }
  }
  return context
}

